import { Component, effect, inject, OnInit } from '@angular/core';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { Commune } from '../../../models/commune';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppUtilityService, generateSlug } from '../../../utility/app.utility';
import { RoutingService } from '../../../services/routing.service';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { CommuneStore } from '../../../stores/commune/commune.state';
import { DividerModule } from 'primeng/divider';
import { EditorModule } from 'primeng/editor';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { NgIf } from '@angular/common';
import { HeaderComponent } from '../../atoms/header/header.component';
import { getState } from '@ngrx/signals';
import { InputGroupModule } from 'primeng/inputgroup';
import { ButtonDirective } from 'primeng/button';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { GlobalsStore } from '../../../stores/globals.state';
import { uniqueSlugValidator } from '../../../validators/uniqueSlug.validator';
import { CommuneService } from '../../../services/commune/commune.service';

@Component({
  selector: 'app-commune-form',
  standalone: true,
  imports: [
    DividerModule,
    EditorModule,
    FormFieldWrapperComponent,
    FormFooterComponent,
    InputSwitchModule,
    InputTextModule,
    NgIf,
    ReactiveFormsModule,
    HeaderComponent,
    InputGroupModule,
    ButtonDirective,
    InputGroupAddonModule,
  ],
  templateUrl: './commune-form.component.html',
  styleUrl: './commune-form.component.scss',
})
export class CommuneFormComponent
  extends BaseFormComponent<Commune>
  implements OnInit {
  override formInitialized = false;
  readonly globalsStore = inject(GlobalsStore);
  readonly store = inject(CommuneStore);
  allSlugs: string[] = [];

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder,
    private communeService: CommuneService
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );
    const state = getState(this.store);
    if (state.selectedId && !state.error) {
      this.itemToEdit = this.store.selectedItem();
      this.initializeForm();
    }

    effect(() => {
      if (state.selectedId || this.itemToEdit) return;
      const globalsState = getState(this.globalsStore);
      if (globalsState.activeCommune) {
        this.initializeForm();
      }
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (!this.formInitialized) this.initializeForm();
  }

  /**
   * Initializes the form with predefined structure and any default or existing values.
   * Sets up form fields with appropriate validators and update strategies.
   *
   * @return {void} This method does not return any value.
   */
  initializeForm(): void {
    this.itemToEdit = this.store.selectedId()
      ? this.store.selectedItem()
      : this.globalsStore.activeCommune() || undefined;

    this.form = this.fb.group({
      slug: [
        this.itemToEdit?.slug || '',
        {
          asyncValidators: [
            uniqueSlugValidator(this.communeService, this.itemToEdit?.slug),
          ],
          updateOn: 'blur', // 'blur' | 'change'
        },
      ],
      name: [this.itemToEdit?.name || '', Validators.required],
      key: [this.itemToEdit?.key || ''],
      contactPersonFirstName: [
        this.itemToEdit?.contactPersonFirstName || '',
        Validators.required,
      ],
      contactPersonLastName: [this.itemToEdit?.contactPersonLastName || ''],
      contactPersonMail: [this.itemToEdit?.contactPersonMail || ''],
      contactPersonPhone: [this.itemToEdit?.contactPersonPhone || ''],
      contactPersonRole: [this.itemToEdit?.contactPersonRole || ''],
      contactPersonSalutation: [this.itemToEdit?.contactPersonSalutation || ''],
      organisation: [this.itemToEdit?.organisation || ''],
      street: [this.itemToEdit?.street || ''],
      zip: [this.itemToEdit?.zip || ''],
      city: [this.itemToEdit?.city || ''],
      title: [this.itemToEdit?.title || ''],
      contactUrl: [this.itemToEdit?.contactUrl || ''],
      locked: [this.itemToEdit?.locked || false],
      thematic: [this.itemToEdit?.thematic || false],
      twoFactorAuth: [this.itemToEdit?.twoFactorAuth || false],
    });
    this.formInitialized = true;
  }

  /**
   * Handles the submission process for a form or specific action.
   *
   * This method calls the `submit` function to execute the necessary operations
   * associated with submission. Ensure that any prerequisite conditions or validations
   * are met before invoking this method.
   *
   * @return {void} This method does not return any value.
   */
  onSubmit(): void {
    this.submit();
    this.globalsStore.setActiveCommune();
  }

  /**
   * Copies the Atlas URL for the current item being edited to the clipboard.
   * The URL is generated using the current window's origin and the item's URL slug.
   * A confirmation message is displayed once the URL is successfully copied.
   *
   * @return {void} This method does not return a value.
   */
  copyAtlasUrlToClipboard(): void {
    const urlToCopy = `${window.location.origin}/${this.form.value.slug}`;
    window.navigator.clipboard.writeText(urlToCopy).then(() => {
      this.confirmationDialogService.showMessage(
        'Atlas-Link kopiert (öffentlich)'
      );
    });
    console.log(this.form);
  }

  /**
   * Generates a URL-friendly slug based on the name of the current item being edited
   * and updates the form control with the generated slug value.
   * The method does nothing if no item is selected for editing.
   *
   * @return {void} Does not return a value.
   */
  generateSlug(): void {
    const slug = generateSlug(
      this.form.value.name,
      this.allSlugs,
      this.itemToEdit?.slug
    );
    this.form.patchValue({ slug });
  }
}
